import { Component } from '@theme/component';

/**
 * slideshow-controls
 * Manages thumbnail/dot/counter navigation state.
 */
class SlideshowControls extends Component {
  /**
   * Activate the control item at `index`.
   * Called by on:click="/select/N" from thumbnails/dots — delegate scroll to parent.
   * @param {number} index
   */
  select(index) {
    // Forward to parent slideshow-component which handles the actual scroll
    const slideshow = /** @type {SlideshowComponent | null} */ (this.closest('slideshow-component'));
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(index);
      return;
    }
    // Fallback: just update our own UI if no parent found
    this.updateUI(index);
  }

  /**
   * Update thumbnail/dot/counter UI to reflect active index.
   * Called by slideshow-component after scrolling.
   * @param {number} index
   */
  updateUI(index) {
    // Thumbnails
    const thumbnails = /** @type {HTMLElement[]} */ (this.refs['thumbnails'] ?? []);
    thumbnails.forEach((t, i) => {
      t.setAttribute('aria-selected', String(i === index));
    });
    if (thumbnails[index]) {
      thumbnails[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    // Dots
    const dots = /** @type {HTMLElement[]} */ (this.refs['dots'] ?? []);
    dots.forEach((d, i) => {
      d.setAttribute('aria-selected', String(i === index));
    });

    // Counter
    const current = /** @type {HTMLElement | undefined} */ (this.refs['current']);
    if (current) current.textContent = String(index + 1);
  }
}

/**
 * slideshow-component
 * Scrollable carousel. Supports select(index), previous(), next().
 */
class SlideshowComponent extends Component {
  #currentIndex = 0;
  #scrollTimer = 0;
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // The CSS hides overflow until [in-viewport] is present.
    // Mark in-viewport immediately so scrolling is enabled.
    this.setAttribute('in-viewport', '');

    const scroller = this.#scroller;
    if (scroller) {
      scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    // Set initial aria-hidden on all slides
    const initial = parseInt(this.getAttribute('initial-slide') || '0', 10);
    requestAnimationFrame(() => {
      this.#applySlideVisibility(initial);
      if (initial > 0) {
        this.#scrollTo(initial, false);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    clearTimeout(this.#scrollTimer);
  }

  get #scroller() {
    return this.querySelector('slideshow-slides');
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('slideshow-slide')]);
  }

  get #controls() {
    return /** @type {SlideshowControls | null} */ (this.querySelector('slideshow-controls'));
  }

  get #slideCount() {
    return this.#slides.length;
  }

  #applySlideVisibility(activeIndex) {
    this.#slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', String(i !== activeIndex));
    });
  }

  #scrollTo(index, animate = true) {
    const scroller = this.#scroller;
    if (!scroller) return;
    const slideWidth = scroller.clientWidth;
    scroller.scrollTo({
      left: slideWidth * index,
      behavior: animate ? 'smooth' : 'instant',
    });
  }

  #onScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = window.setTimeout(() => {
      const scroller = this.#scroller;
      if (!scroller) return;
      const slideWidth = scroller.clientWidth;
      if (!slideWidth) return;
      const index = Math.round(scroller.scrollLeft / slideWidth);
      if (index !== this.#currentIndex) {
        this.#currentIndex = index;
        this.#applySlideVisibility(index);
        this.#controls?.updateUI(index);
        this.#updateArrows();
      }
    }, 50);
  };

  #updateArrows() {
    const infinite = this.hasAttribute('infinite');
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="previous"]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="next"]'));
    if (!infinite) {
      if (prev) prev.disabled = this.#currentIndex <= 0;
      if (next) next.disabled = this.#currentIndex >= this.#slideCount - 1;
    } else {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
    }
  }

  /**
   * Select a slide by index.
   * @param {number} index
   * @param {Event | undefined} [_event]
   * @param {{ animate?: boolean }} [options]
   */
  select(index, _event, options = {}) {
    const count = this.#slideCount;
    const infinite = this.hasAttribute('infinite');

    if (infinite) {
      if (index < 0) index = count - 1;
      if (index >= count) index = 0;
    } else {
      index = Math.max(0, Math.min(count - 1, index));
    }

    this.#currentIndex = index;
    this.setAttribute('actioned', '');

    const animate = options.animate !== false;
    this.#scrollTo(index, animate);
    this.#applySlideVisibility(index);
    this.#controls?.updateUI(index);
    this.#updateArrows();
  }

  /** Go to previous slide. */
  previous(_event) {
    this.select(this.#currentIndex - 1);
  }

  /** Go to next slide. */
  next(_event) {
    this.select(this.#currentIndex + 1);
  }
}

if (!customElements.get('slideshow-controls')) {
  customElements.define('slideshow-controls', SlideshowControls);
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', SlideshowComponent);
}
